import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import { reconstructOrderTraderSide } from '@/lib/kalshiApi/orderReadV2';

// Per-ticker view of the logged-in user's open Kalshi exposure.
// Positions are summed per side so the POS chip can render a proportional
// YES/NO arc when a user holds both. Orders carry their Kalshi-native
// queue_position; cumulative "level + better" depth is computed at render
// time against the live orderbook (so it ticks with hydra without a refetch).
//
// Wire: a single relay round-trip per poll. /api/kalshi/me/snapshot returns
// {positions, orders} pre-filtered to the calling user and (when supplied)
// to the visible ticker set. queue_position_fp is merged into each order
// server-side so the hook just normalizes one payload.

export interface UserPositionSlice {
  yesCount: number;
  noCount: number;
}

export interface UserOrderSlice {
  orderId: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  priceCents: number;
  remainingCount: number;
  queuePosition: number | null;
}

export interface UserKalshiPOS {
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface RawPosition {
  ticker?: unknown;
  side?: unknown;
  netCount?: unknown;
}

interface RawOrder {
  order_id?: unknown;
  ticker?: unknown;
  outcome_side?: unknown;
  side?: unknown;
  action?: unknown;
  yes_price_dollars?: unknown;
  no_price_dollars?: unknown;
  remaining_count_fp?: unknown;
  yes_price?: unknown;
  no_price?: unknown;
  remaining_count?: unknown;
  queue_position_fp?: unknown;
  queue_position?: unknown;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}
function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function asFloat(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function extractList<T>(payload: unknown, key: string): T[] {
  if (!payload || typeof payload !== 'object') return [];
  const list = (payload as Record<string, unknown>)[key];
  return Array.isArray(list) ? (list as T[]) : [];
}

const POLL_MS = 5000;

export function useUserKalshiPOS(tickers: string[]): UserKalshiPOS {
  const [positionByTicker, setPositionByTicker] = useState<Map<string, UserPositionSlice>>(
    () => new Map(),
  );
  const [ordersByTicker, setOrdersByTicker] = useState<Map<string, UserOrderSlice[]>>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tickerKey = useMemo(() => tickers.slice().sort().join('|'), [tickers]);
  const tickerKeyRef = useRef(tickerKey);
  tickerKeyRef.current = tickerKey;

  const fetchAll = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const url = tickerKeyRef.current
        ? `/api/kalshi/me/snapshot?market_tickers=${encodeURIComponent(tickerKeyRef.current.replace(/\|/g, ','))}`
        : '/api/kalshi/me/snapshot';
      const res = await apiFetch(url);
      const json: unknown = res.ok ? await res.json() : null;

      const rawPositions = extractList<RawPosition>(json, 'positions');
      const rawOrders = extractList<RawOrder>(json, 'orders');

      const posMap = new Map<string, UserPositionSlice>();
      for (const p of rawPositions) {
        const ticker = asString(p.ticker);
        const side = asString(p.side);
        const net = asNumber(p.netCount);
        if (!ticker || !net || net <= 0) continue;
        if (side !== 'yes' && side !== 'no') continue;
        const slice = posMap.get(ticker) ?? { yesCount: 0, noCount: 0 };
        if (side === 'yes') slice.yesCount += net;
        else slice.noCount += net;
        posMap.set(ticker, slice);
      }

      const ordMap = new Map<string, UserOrderSlice[]>();
      for (const o of rawOrders) {
        const orderId = asString(o.order_id);
        const ticker = asString(o.ticker);
        // Reconstruct the trader-native {side, action} for DISPLAY. V2 is single-
        // book: a resting buy-NO reads back side:'yes'/action:'sell'/outcome_side:'no',
        // so reading legacy `side` first mislabeled the overlay's NO orders as YES
        // on the wrong ladder leg. reconstructOrderTraderSide derives side from
        // outcome_side and flips action when cross-framed — the same adapter
        // parseOrders uses. Orders only; fills keep the legacy pair.
        const { side: sideRaw, action: actionRaw } = reconstructOrderTraderSide(o);
        const remaining =
          asFloat(o.remaining_count_fp) ?? asNumber(o.remaining_count) ?? 0;
        if (!orderId || !ticker || remaining <= 0) continue;
        const yesDollars = asFloat(o.yes_price_dollars);
        const noDollars = asFloat(o.no_price_dollars);
        let yesPx = yesDollars != null ? Math.round(yesDollars * 100) : asNumber(o.yes_price);
        let noPx = noDollars != null ? Math.round(noDollars * 100) : asNumber(o.no_price);
        // Kalshi frequently sends only ONE leg's price — a NO order commonly
        // arrives as `yes_price` only (CLAUDE.md venue quirk: "derive 100 − yes_price
        // when the side-matching field is missing or zero"). Derive the missing/zero
        // side from the complement so a resting NO order isn't silently dropped from
        // the POS overlay. Mirrors PositionSidebar / accountStore / TraderDetail.
        if ((yesPx == null || yesPx === 0) && noPx != null && noPx !== 0) yesPx = 100 - noPx;
        if ((noPx == null || noPx === 0) && yesPx != null && yesPx !== 0) noPx = 100 - yesPx;
        const priceCents = sideRaw === 'yes' ? yesPx : noPx;
        if (priceCents == null) continue;
        const queuePosition = asFloat(o.queue_position_fp) ?? asNumber(o.queue_position);
        const slice: UserOrderSlice = {
          orderId,
          side: sideRaw,
          action: actionRaw,
          priceCents,
          remainingCount: remaining,
          queuePosition,
        };
        const list = ordMap.get(ticker) ?? [];
        list.push(slice);
        ordMap.set(ticker, list);
      }

      setPositionByTicker(posMap);
      setOrdersByTicker(ordMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
    const id = setInterval(() => {
      void fetchAll();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [fetchAll, tickerKey]);

  return { positionByTicker, ordersByTicker, loading, error, refresh: fetchAll };
}
